/* eslint-disable @typescript-eslint/no-explicit-any */
import { inject, Injectable } from '@angular/core';
import { PatientDataService, PatientDetails } from '@ffd/api-client';
import { DataState, DISABLE_GLOBAL_ERROR_TOASTS, FfdComponentStore } from '@libs/utils';
import { Actions } from '@ngrx/effects';
import { distinctUntilChanged, EMPTY, filter, of, switchMap, tap } from 'rxjs';
import {
  patientsActions,
  selectPatientsData,
  selectPatientsDataState,
  selectRecentPatients,
  selectTotalPatientCount,
  selectUserSiteId,
} from '@libs/store';
import { Store } from '@ngrx/store';
import { tapResponse, concatLatestFrom } from '@ngrx/operators';
import { HttpContext } from '@angular/common/http';
import { TranslateService } from '@ngx-translate/core';
import { ToastService } from '@libs/shared-services';

const MINIMUM_SEARCH_LENGTH = 1;

export interface PatientSelectorState {
  siteId: string;
  patient?: PatientDetails;
  patients: PatientDetails[];
  totalPatientsCount: number;
  lastPatientSearchTerm: string;
}

export interface PatientSelectorViewModel {
  siteId: string;
  patient?: PatientDetails;
  patients: PatientDetails[];
  patientsAreLoading: boolean;
}

const initialState: PatientSelectorState = {
  siteId: '',
  patients: [],
  totalPatientsCount: 0,
  lastPatientSearchTerm: '',
};

@Injectable({
  providedIn: 'root',
})
export class PatientSelectorService extends FfdComponentStore<PatientSelectorState, PatientSelectorViewModel> {
  private readonly store = inject(Store);
  private readonly patientDataService = inject(PatientDataService);
  private readonly toastService = inject(ToastService);
  private readonly translateService = inject(TranslateService);
  private readonly actions$ = inject(Actions);

  constructor() {
    super(initialState);
  }

  private readonly selectedSiteId = this.store.selectSignal(selectUserSiteId);

  public init(): void {
    this.setDefaultSelectedPatient();
  }

  public setPatient = this.updater((state: PatientSelectorState, patient: PatientDetails): PatientSelectorState => {
    return {
      ...state,
      patient,
    };
  });

  public setPatients = this.updater((state: PatientSelectorState, patients: PatientDetails[]): PatientSelectorState => {
    return {
      ...state,
      patients,
    };
  });

  protected setLastPatientSearchTerm = this.updater((state, lastSearchTerm: string): PatientSelectorState => {
    return {
      ...state,
      lastPatientSearchTerm: lastSearchTerm,
    };
  });

  public updatePatientSearchTerm(searchTerm: string, searchInEmails?: boolean): void {
    this.setLastPatientSearchTerm(searchTerm);
    if (searchTerm.length >= MINIMUM_SEARCH_LENGTH) {
      this.store.dispatch(patientsActions.onSetSearchTerm({ searchTerm, searchInEmails }));
    }
  }

  public clearPatientSearchTerm(): void {
    this.patchState({ ...initialState });
    this.store.dispatch(patientsActions.onSetSearchTerm({ searchTerm: '' }));
  }

  public readonly searchPatients = this.effect<{ searchTerm?: string; searchInEmails: boolean }>(($) =>
    $.pipe(
      concatLatestFrom(() => [
        this.select((state) => state.patients),
        this.store.select(selectTotalPatientCount),
        this.select((state) => state.lastPatientSearchTerm),
      ]),
      distinctUntilChanged((a, b) => {
        return a[0].searchTerm === b[0].searchTerm && a[1]?.length === b[1]?.length;
      }),
      filter(([_, patients, totalCount]) => {
        return !totalCount || totalCount > (patients?.length ?? 0);
      }),
      switchMap(([{ searchInEmails }, __, ___, lastSearchTerm]) => {
        this.store.dispatch(patientsActions.onSetSearchTerm({ searchTerm: lastSearchTerm, searchInEmails }));
        return of(EMPTY);
      }),
    ),
  );

  public readonly getPatient = this.effect<{ patientId: string; callback?: (patient: PatientDetails) => void }>(($) =>
    $.pipe(
      concatLatestFrom(() => this.store.select(selectUserSiteId)),
      switchMap(([{ patientId, callback }, siteId]) => {
        return this.patientDataService
          .getPatientBySite(siteId ?? '', patientId, undefined, undefined, {
            context: new HttpContext().set(DISABLE_GLOBAL_ERROR_TOASTS, true),
          })
          .pipe(
            tapResponse(
              (patient: PatientDetails) => {
                this.setPatient(patient);
                callback?.(patient);
              },
              () => {
                this.toastService.error(this.translateService.instant('error_message_server_error_patient_by_site'));
                return null;
              },
            ),
          );
      }),
    ),
  );

  public readonly setDefaultSelectedPatient = this.effect(($) =>
    $.pipe(
      switchMap(() =>
        this.store.select(selectRecentPatients).pipe(
          concatLatestFrom(() => this.store.select(selectPatientsData)),
          tap(([recentPatients, patients]) => {
            if (recentPatients.length === 0 && patients && patients.length > 0) {
              this.setPatient(patients[0]);
            } else if (recentPatients.length > 0 && this.state().patient?.id !== recentPatients[0].patientId) {
              this.getPatient({
                patientId: recentPatients?.[0]?.patientId ?? '',
              });
            }
          }),
        ),
      ),
    ),
  );

  protected mapStateToViewModel(state: PatientSelectorState): PatientSelectorViewModel {
    const patients = this.store.selectSignal(selectPatientsData)();
    const patientsDataState = this.store.selectSignal(selectPatientsDataState)();
    const recentPatients = this.store.selectSignal(selectRecentPatients)();
    return {
      ...state,
      patients:
        (state.lastPatientSearchTerm
          ? patients
          : recentPatients.map((recentPatient) => ({
              id: recentPatient.patientId,
              firstName: recentPatient.firstName,
              lastName: recentPatient.lastName,
              primaryProvider: null as unknown as any,
            }))) ?? [],
      patient: state.patient,
      patientsAreLoading: state.lastPatientSearchTerm ? patientsDataState === DataState.Loading : false,
    };
  }
}
